import type { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

import axios from 'axios';

import { paths } from 'src/routes/paths';

import { CONFIG } from 'src/global-config';

import { JWT_STORAGE_KEY, JWT_REFRESH_STORAGE_KEY } from 'src/auth/context/jwt/constant';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: CONFIG.serverUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use((config) => {
  const token = sessionStorage.getItem(JWT_STORAGE_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ----------------------------------------------------------------------
// Silent access-token refresh.
//
// When a request 401s (the ~3-day admin access token expired), we exchange the
// stored refresh token for a fresh access token and replay the request once.
// If refresh fails (no/expired refresh token), we clear the session and send the
// user to sign-in -- no "Token expired!" alert. All concurrent 401s share ONE
// in-flight refresh so we never fire the refresh endpoint many times at once.
// ----------------------------------------------------------------------

/** Wipe the admin session and redirect to sign-in (no popup). */
function forceSignOut() {
  sessionStorage.removeItem(JWT_STORAGE_KEY);
  sessionStorage.removeItem(JWT_REFRESH_STORAGE_KEY);
  if (
    typeof window !== 'undefined' &&
    !window.location.pathname.startsWith(paths.auth.jwt.signIn)
  ) {
    window.location.href = paths.auth.jwt.signIn;
  }
}

/**
 * Exchange the stored refresh token for a new access token. Returns the new
 * token, or null when refresh isn't possible (no token, or the server rejects
 * it). Uses a bare axios call so it never re-enters this interceptor.
 */
export async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = sessionStorage.getItem(JWT_REFRESH_STORAGE_KEY);
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await axios.post(`${CONFIG.serverUrl}${endpoints.auth.refresh}`, {
      refreshToken,
    });
    const newAccessToken: string | undefined = response.data?.accessToken;
    if (!newAccessToken) {
      return null;
    }
    sessionStorage.setItem(JWT_STORAGE_KEY, newAccessToken);
    return newAccessToken;
  } catch {
    return null;
  }
}

let refreshPromise: Promise<string | null> | null = null;

/** Single-flight wrapper so parallel 401s trigger only one refresh call. */
function getRefreshedAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/** One failed field of a validation rejection, as the API's exceptions filter shapes it. */
export type ApiErrorDetail = { field: string; constraints: string[] };

/** The API's error envelope: `{ success: false, statusCode, errorCode, message, details }`. */
type ApiErrorEnvelope = {
  message?: string;
  errorCode?: string;
  details?: ApiErrorDetail[] | null;
};

/** What this instance rejects with: a plain Error carrying the envelope's specifics. */
export type ApiError = Error & {
  status?: number;
  errorCode?: string;
  details?: ApiErrorDetail[] | null;
};

axiosInstance.interceptors.response.use(
  /*
    Unwrap the API's success envelope.

    The backend answers `{ success, statusCode, data }` on every 2xx. Peeling
    `data` off here keeps `response.data` meaning "the payload", so every
    existing slice and SWR fetcher works unchanged — the envelope is a
    transport concern and stops at this line.

    Guarded rather than unconditional: a 204 has no body, and anything that is
    not the envelope (a binary download, a proxy error page) passes through
    untouched instead of becoming `undefined`.
  */
  (response) => {
    const body = response.data;
    const isEnvelope =
      body !== null && typeof body === 'object' && 'success' in body && 'data' in body;

    if (isEnvelope) {
      response.data = (body as { data: unknown }).data;
    }

    return response;
  },
  async (error) => {
    const originalRequest = error?.config as RetriableRequestConfig | undefined;
    const status = error?.response?.status;

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      const newAccessToken = await getRefreshedAccessToken();
      if (newAccessToken) {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      }

      // Refresh impossible or rejected -> the session is truly over. Log out
      // quietly instead of surfacing an alert.
      forceSignOut();
    }

    const envelope = error?.response?.data as Partial<ApiErrorEnvelope> | undefined;
    const message = envelope?.message || error?.message || 'Something went wrong!';
    console.error('Axios error:', message);
    // The rejected Error keeps the envelope's `errorCode` and `details` (the
    // per-field constraints of a validation failure) so a caller can show the
    // precise reason. Every existing caller reads only `.message`, unchanged.
    const apiError: ApiError = Object.assign(new Error(message), {
      status: typeof status === 'number' ? status : undefined,
      errorCode: envelope?.errorCode,
      details: envelope?.details ?? null,
    });
    return Promise.reject(apiError);
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async <T = unknown>(
  args: string | [string, AxiosRequestConfig]
): Promise<T> => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args, {}];

    const res = await axiosInstance.get<T>(url, config);

    return res.data;
  } catch (error) {
    console.error('Fetcher failed:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  maps: {
    nearbyGyms: '/maps/nearby-gyms',
    search: '/maps/search',
  },
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/admin-users-auth/me',
    signIn: '/admin-users-auth/login',
    refresh: '/admin-users-auth/refresh',
    forgotPassword: '/admin-users-auth/forgot-password',
    resetPassword: '/admin-users-auth/reset-password',
  },
  admin: {
    appUsers: {
      registrations: '/admin/app-users/registrations',
      activeUsers: '/admin/app-users/total-active-users',
      appUsers: '/admin/app-users',
      suggested: '/admin/app-users-suggested',
      // KAN-747 — the audience of a « sans localisation » communication.
      withoutLocation: '/admin/app-users/without-location',
    },
    adminUsers: '/admin-users',
    appMobile: {
      currentDownload: '/admin/app-mobile/current-download',
      totalDownloads: '/admin/app-mobile/total-downloads',
      signupRate: '/admin/app-mobile/signup-rate',
      dailyDownloads: '/admin/app-mobile/daily-downloads',
      resyncStoreDownloads: '/admin/app-mobile/store-downloads/resync',
    },
    places: '/admin/places',
    placeMarkers: {
      root: '/admin/place-markers',
      sprites: '/admin/place-markers/sprites',
    },
    enterprises: '/admin/enterprises',
    geo: '/admin/geo',
    reports: {
      // KAN-630 — the backend serves this at `reports/publications`. Nothing
      // has ever answered `reports/trainings`, so every request the reports
      // dashboard made for publication reports 404'd: the list was empty, the
      // detail page never loaded, and no status change was ever saved. The
      // entity was renamed from Training to Publication and this constant was
      // not.
      trainings: '/reports/publications',
      places: '/reports/places',
      users: '/reports/users',
    },
    support: {
      tickets: '/admin/support-tickets',
    },
    communications: '/admin/communications',
    publicity: '/admin/publicity',
    feedback: {
      accountDeletion: '/admin/feedback/account-deletion',
    },
    // KAN-755 — the acquisition funnel.
    acquisition: {
      funnel: '/admin/acquisition/funnel',
    },
    // KAN-788 — the website's event pages.
    siteEvents: {
      list: '/admin/site-events',
      event: (id: string) => `/admin/site-events/${id}`,
      status: (id: string) => `/admin/site-events/${id}/status`,
      cover: (id: string) => `/admin/site-events/${id}/cover`,
      images: (id: string) => `/admin/site-events/${id}/images`,
      imagesOrder: (id: string) => `/admin/site-events/${id}/images/order`,
      image: (id: string, imageId: string) => `/admin/site-events/${id}/images/${imageId}`,
      shareCard: (id: string) => `/admin/site-events/${id}/share-card`,
    },
    debug: {
      explore: {
        // Every user with a saved location + the OnFit / Match graph.
        map: '/admin/debug/explore/map',
        // One viewer's deck, reproduced and explained candidate by candidate.
        viewer: (id: string) => `/admin/debug/explore/viewers/${id}`,
        // One pair, both directions, with the 15-step checklist.
        pair: (viewerId: string, targetId: string) =>
          `/admin/debug/explore/viewers/${viewerId}/pairs/${targetId}`,
      },
    },
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
};
